import React, { useState } from "react";
import { Loader2, History, CheckCircle2, XCircle, Bot, MousePointerClick } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

/**
 * Dialog historique des syncs Nayax (toutes machines confondues).
 *
 * Récupère `GET /nayax/sync-logs?limit=50`. Affiche timestamp, source (manuel/scheduler),
 * statut (vert/rouge), compteurs (créés/skippés/échoués) et erreurs détaillées (expand).
 */
export default function NayaxSyncLogsDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [distMap, setDistMap] = useState({});
  const [expanded, setExpanded] = useState({});

  React.useEffect(() => {
    if (!open) {
      setLogs([]);
      setExpanded({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/nayax/sync-logs?limit=50"),
      api.get("/distributors"),
    ]).then(([logsRes, distRes]) => {
      if (cancelled) return;
      setLogs(logsRes.data.logs || []);
      const m = {};
      for (const d of distRes.data) m[d.id] = d.name;
      setDistMap(m);
    }).catch(() => {
      // silently ignore
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [open]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("fr-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="nayax-logs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-[#1F4E3D]" />
            Historique des synchronisations Nayax
          </DialogTitle>
          <DialogDescription>
            50 dernières syncs (manuelles + planificateur 15 min)
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-[#575A57]">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Chargement…
          </div>
        ) : logs.length === 0 ? (
          <div className="py-8 text-center text-[#575A57]">
            Aucune synchronisation enregistrée pour l'instant.
          </div>
        ) : (
          <div className="border border-[#E5E5E0] rounded max-h-96 overflow-y-auto" data-testid="nayax-logs-table">
            {logs.map((log) => (
              <div
                key={log.id}
                className="px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 text-sm hover:bg-[#F9F9F6]"
                data-testid={`nayax-log-row-${log.id}`}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-shrink-0">
                    {log.had_error ? (
                      <XCircle className="w-4 h-4 text-[#C05C47]" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 text-[#1F4E3D]" />
                    )}
                  </div>
                  <div className="text-xs font-mono text-[#575A57] w-28">
                    {fmtDate(log.created_at)}
                  </div>
                  <div className="flex-1 min-w-[120px] font-medium text-[#1A1C1A]">
                    {distMap[log.distributor_id] || log.distributor_id.slice(0, 8) + "…"}
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${log.source === "scheduler" ? "bg-[#F1EAF8] text-[#5B3A8C] border-[#C9B5E0]" : "bg-[#E8EEF6] text-[#1F3A66] border-[#A8BBD6]"}`}
                  >
                    {log.source === "scheduler" ? (
                      <><Bot className="w-3 h-3 mr-1" /> Auto</>
                    ) : (
                      <><MousePointerClick className="w-3 h-3 mr-1" /> Manuel</>
                    )}
                  </Badge>
                  <div className="text-xs flex items-center gap-2 flex-shrink-0">
                    <span className="text-[#575A57]">
                      {log.transactions_received} tx
                    </span>
                    <span className="text-[#1F4E3D] font-semibold">
                      +{log.sales_created}
                    </span>
                    {log.skipped_duplicates > 0 && (
                      <span className="text-[#A3A6A3]" title="Doublons skippés">
                        ⊘{log.skipped_duplicates}
                      </span>
                    )}
                    {log.failed > 0 && (
                      <button
                        onClick={() => setExpanded((p) => ({ ...p, [log.id]: !p[log.id] }))}
                        className="text-[#C05C47] underline"
                        data-testid={`nayax-log-expand-${log.id}`}
                      >
                        ⚠ {log.failed} erreur(s)
                      </button>
                    )}
                  </div>
                </div>
                {expanded[log.id] && log.errors && log.errors.length > 0 && (
                  <ul className="mt-2 ml-7 text-[11px] text-[#A04E2C] list-disc pl-4 space-y-0.5">
                    {log.errors.map((err, i) => (
                      <li key={i} className="font-mono">{err}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
